import React, { useEffect, useState, useMemo } from "react";
import {
  ArchiveItemDTO,
  DebtorClaimStage,
  SendDebtOverviewConversationAction,
} from "../../../../api/client";
import { Step } from "../ConversationSupervisorStep";
import StepZilla from "react-stepzilla";
import { actionCreators as ConversationSupervisorStoreActionCreators } from "../../../../store/ConversationSupervisorStore";
import { getActiveLeapClaimsForDebtOverview } from "../../../../store/Selectors";
import { Box, TextField, Button, Modal } from "@mui/material";
import { pdf, PDFViewer } from "@react-pdf/renderer";
import { MuiChipsInput } from "mui-chips-input";
import { useAppDispatch, useAppSelector } from "../../../../hooks";
import { DebtOverviewPDFDocument } from "../../../debtOverview/DebtOverview";
import { validateEmailAddress } from "../../../Utils";
import { actionCreators as ArchiveStoreActionCreators } from "../../../../store/ArchiveStore";
import { actionCreators as NotificationStore } from "../../../../store/NotificationsStore";
import CircularProgress from "@mui/material/CircularProgress";

interface InjectedProps {
  jumpToStep?: typeof StepZilla.jumpToStep;
}

const SendDebtOverview = (props: InjectedProps) => {
  const dispatch = useAppDispatch();

  const [open, setOpen] = useState(false);
  const leapClaims = useAppSelector(getActiveLeapClaimsForDebtOverview);
  const isLoadingArchiveId = useAppSelector((state) => state.archive.isLoadingArchiveId);
  const archiveId = useAppSelector((state) => state.archive.archiveId);
  const clearArchiveId = ArchiveStoreActionCreators.clearArchiveId;
  const addNotification = NotificationStore.addNotification;

  const debtCollections = useMemo(() => {
    return leapClaims.filter(
      (c) =>
        (c.claimStage === DebtorClaimStage.DebtCollection ||
          c.claimStage === DebtorClaimStage.DebtCollectionWarning) &&
        c.displayOnDebtOverview
    );
  }, [leapClaims]);

  const nonDebtCollections = useMemo(() => {
    return leapClaims.filter(
      (c) =>
        c.claimStage !== DebtorClaimStage.DebtCollection &&
        c.claimStage !== DebtorClaimStage.DebtCollectionWarning &&
        c.displayOnDebtOverview
    );
  }, [leapClaims]);

  const debtOverviewPdfDocument = <DebtOverviewPDFDocument claims={nonDebtCollections} debtCollections={debtCollections}/>

  useEffect(() => {dispatch(clearArchiveId())}, [clearArchiveId, dispatch])

  const pdfAsBase64 = async (): Promise<string | undefined> => {
    try {
      const blob = await pdf(debtOverviewPdfDocument).toBlob();
      if (!blob) return undefined;

      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64data = reader.result as string;
          const base64Content = base64data.split(",")[1]; // Removes metadata
          resolve(base64Content); // Returns base64Content
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error generating Base64 PDF:", error);
      return undefined;
    }
  };


  const [emailAddresses, setEmailAddresses] = useState<string[]>([]);
  const [introductionText, setIntroductionText] = useState(`Hei,

Sender som avtalt oversikt over utestående krav. Se vedlagt PDF.`);
  const emailFooter = `+47 22 80 70 70

CrediCare AS
Postboks 483 Skøyen
NO-0213 Oslo
Norway
https://credicare.no`;

  const isValidEmailAddresses = () => {
    return emailAddresses.length > 0;
  };

  const handleChangeOnIntroductionText = (event) => {
    setIntroductionText(event.target.value);
  };

  const handleOnEmailAddressAdded = (value: string) => {
    if (validateEmailAddress(value)) {
      setEmailAddresses([...emailAddresses, value]);
    }
  };

  const handleOnEmailAddressDeleted = (value: string) => {
    setEmailAddresses(emailAddresses.filter((c) => c !== value));
  };


  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const pdfBase64 = await pdfAsBase64();
      if (!pdfBase64) {
        dispatch(addNotification({message: "Kunne ikke generere PDF. Prøv igjen.", variant: "error"}))
        return;
      }

      const archiveItemDTO: ArchiveItemDTO = {
        blob: pdfBase64,
        filename: "Oversikt",
        type: "pdf",
      };

      const registerArchiveItem = ArchiveStoreActionCreators.registerArchiveItem;
      dispatch(registerArchiveItem(archiveItemDTO));
    } catch (error) {
      dispatch(addNotification({message: "Kunne ikke sende oversikten. Prøv igjen.", variant: "error"}));
    }
  };

  useEffect(() => {
    if (!isLoadingArchiveId && archiveId) {
      const action: SendDebtOverviewConversationAction = {
        emailAddresses: emailAddresses,
        archiveIds: [archiveId],
        additionalText: `${introductionText}`,
        selectedDebtorClaims: [...nonDebtCollections, ...debtCollections],
        discriminator: "SendDebtOverviewConversationAction",
      };

      const addConversationSupervisorAction = ConversationSupervisorStoreActionCreators.addConversationAction;
      dispatch(addConversationSupervisorAction(action));
      props.jumpToStep(Step.Summary);
      dispatch(clearArchiveId());
    }
  }, [isLoadingArchiveId, archiveId, emailAddresses, introductionText, nonDebtCollections, debtCollections, dispatch, props, clearArchiveId]);



  return (
    <React.Fragment>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingTop: "10px",
        }}
      >
        <MuiChipsInput
          disableEdition={true}
          label="E-postadresser"
          value={emailAddresses}
          onAddChip={handleOnEmailAddressAdded}
          onDeleteChip={handleOnEmailAddressDeleted}
          sx={{ marginTop: "10px" }}
          hideClearAll={true}
          maxRows={1}
          addOnBlur={true}
        />
        <TextField
          label="Introduksjon"
          margin="normal"
          multiline={true}
          maxRows={10}
          value={introductionText}
          onChange={handleChangeOnIntroductionText}
        />

        <Button
          onClick={() => setOpen(true)}
          variant="outlined"
          sx={{ color: "black", margin: 0 }}
        >
          Forhåndsvis PDF
        </Button>

        <Modal
          open={open}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              backgroundColor: "white",
              boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.25)",
              borderRadius: "8px",
              padding: "0 16px 16px 16px",
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Button variant="contained" onClick={() => setOpen(false)}>
                Lukk
              </Button>
            </div>
            <PDFViewer style={{ width: "40vw", height: "90vh" }}>
              {debtOverviewPdfDocument}
            </PDFViewer>
          </Box>
        </Modal>

        <TextField
          label="Signatur e-post"
          margin="normal"
          multiline={true}
          maxRows={4}
          value={emailFooter}
          disabled={true}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="contained"
          onClick={() => props.jumpToStep(Step.Start)}
        >
          Avbryt
        </Button>
        <Button
          variant="contained"
          id={"submit"}
          onClick={handleSubmit}
          disabled={!isValidEmailAddresses() || isLoadingArchiveId}
          type="submit"
          sx={{ marginLeft: '8px' }}
        >
          {isLoadingArchiveId ? <CircularProgress size={16} /> : "OK"}
        </Button>
      </Box>
    </React.Fragment>
  );
};

export default SendDebtOverview;
